import { Capacitor } from '@capacitor/core';
import api from '@/lib/api';

/**
 * Push notification registration for the native customer app.
 *
 * Flow:
 *   1. Request OS permission (Android 13+ needs runtime POST_NOTIFICATIONS).
 *   2. register() → fires 'registration' with the FCM/APNs token.
 *   3. POST the token to the backend so order-status pushes can target this
 *      device. The backend endpoint is best-effort — a 404/501 is swallowed
 *      so a missing endpoint never breaks login.
 *
 * No-ops on web. Requires google-services.json (Android) / APNs key (iOS) to
 * actually deliver; without those the registration simply never fires a token.
 */

let registered = false;

export async function registerPushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform() || registered) return;
  registered = true;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') {
    registered = false; // allow a later retry if the user enables it in settings
    return;
  }

  PushNotifications.addListener('registration', async (token) => {
    try {
      await api.post('/notifications/register-device', {
        token: token.value,
        platform: Capacitor.getPlatform(),
      });
    } catch {
      // Endpoint not present / offline — non-fatal. Token will re-register
      // on next app launch.
    }
  });

  PushNotifications.addListener('registrationError', () => {
    registered = false;
  });

  await PushNotifications.register();
}

/** Called on logout so the device stops receiving the prior user's pushes. */
export async function removePushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
    await api.post('/notifications/unregister-device', {
      platform: Capacitor.getPlatform(),
    });
  } catch {
    /* non-fatal */
  } finally {
    registered = false;
  }
}
